/**
 * This module provides higher-order components to bind action components to the current
 * selection state within an `ItemsScope`.
 */

import { currentItemFacet, selectedItemsFacet } from '../../facets/ScopeFacets';
import { IItemCache, IItemCacheDecorator, IItemCacheSelector } from '../../models/IItemCacheObserver';
import { connectToItemCache, IConnectToItemCacheDecorator } from './connectToItemCache';
import { selectedItemsControl } from '../../controls/selectedItemsControl';
import { IDecoratedProps, withDisplayName } from '../../utilities/components/Decorators';
import { IItemsScopeConnectorProps, getItemsScope } from './connectToItemsScope';
import { getItemSetKey } from '../../models/items/getItemSetKey';
import { getItemKey } from '../../models/items/getItemKey';
import { getCurrentItemSetKey } from '../../models/itemsScope/getCurrentItemSetKey';

/**
 * @public
 */
export interface ISpecifiedItemsProps {
  itemKeys: string[];
}

const NO_ITEMS: string[] = [];

function base<TProps>(itemCache: IItemCache, props: TProps): TProps {
  return props;
}

function decorate<TInjectedProps, TOwnProps>(
  mapItemCacheToProps: IItemCacheSelector<TInjectedProps, TOwnProps>
): IItemCacheDecorator<TInjectedProps, TOwnProps> {
  return <TResult extends {}, P extends TInjectedProps>(
    selector: IItemCacheSelector<TResult, P>
  ) => {
    return (itemCache: IItemCache, props: IDecoratedProps<P, TInjectedProps, TOwnProps>) => {
      const injectedProps = mapItemCacheToProps(itemCache, props);

      return selector(itemCache, {
        ...(props as {}),
        ...(injectedProps as {})
      } as P) as TResult;
    };
  };
}

function decorateWithItemsScope(): IItemCacheDecorator<IItemsScopeConnectorProps, {}> {
  return decorate(getItemsScope);
}

function decorateWithSelectedItems(): IItemCacheDecorator<ISpecifiedItemsProps, {}> {
  const getSelectedItems = (itemCache: IItemCache, props: {
    itemsScopeItemKey?: string;
  }): ISpecifiedItemsProps => {
    const { itemsScopeItemKey } = props;

    if (!itemsScopeItemKey) {
      return {
        itemKeys: NO_ITEMS
      };
    }

    const controlHandler = itemCache.demandItemFacet(selectedItemsControl, undefined);

    const selectedItems = itemCache.demandItemFacet(selectedItemsFacet, itemsScopeItemKey);

    const {
      itemKeys
    } = selectedItemsControl.evaluate(controlHandler)(itemCache, {
      itemsScopeItemKey: itemsScopeItemKey,
      itemKeys: selectedItems && selectedItems.itemKeys || NO_ITEMS
    });

    return {
      itemKeys
    };
  };

  return decorate(decorateWithItemsScope()(getSelectedItems));
}

/**
 * @public
 */
export type IDecorateWithSelectedItemsProps<TProps extends ISpecifiedItemsProps> = IDecoratedProps<TProps, ISpecifiedItemsProps, {}>;

/**
 * Binds a component to the current selected item keys.
 * @public
 */
export function withSelectedItems(): IConnectToItemCacheDecorator<ISpecifiedItemsProps, {}> {
  return <P extends ISpecifiedItemsProps>(
    component: React.ComponentType<P>
  ) => {
    return withDisplayName('WithSelectedItems')(
      connectToItemCache(
        decorateWithSelectedItems()(base)
      )
    )(component);
  };
}

/**
 * @public
 */
export interface ISpecifiedItemProps {
  itemKey?: string | undefined;
}

function decorateWithCurrentItem(): IItemCacheDecorator<ISpecifiedItemProps, {}> {
  const getCurrentItem = (itemCache: IItemCache, props: IItemsScopeConnectorProps): ISpecifiedItemProps => {
    const currentItem = itemCache.demandItemFacet(currentItemFacet, props.itemsScopeItemKey);

    return {
      itemKey: getItemKey(itemCache, {
        itemKey: currentItem && currentItem.itemKey
      })
    };
  };

  return decorate(decorateWithItemsScope()(getCurrentItem));
}

/**
 * @public
 */
export type IDecorateWithCurrentItemProps<TProps extends ISpecifiedItemProps> = IDecoratedProps<TProps, ISpecifiedItemProps, {}>;

/**
 * Binds a component to the current item as a fallback for no input items.
 * @public
 */
export function withCurrentItem(): IConnectToItemCacheDecorator<ISpecifiedItemProps, {}> {
  return <P extends ISpecifiedItemProps>(
    component: React.ComponentType<P>
  ) => {
    return withDisplayName('WithCurrentItem')(
      connectToItemCache(
        decorateWithCurrentItem()(base)
      )
    )(component);
  };
}

/**
 * @beta
 */
export { getItemKey };

/**
 * @beta
 */
export { getItemSetKey };

/**
 * @beta
 */
export { getCurrentItemSetKey };

function decorateWithCurrentItemSet(): IItemCacheDecorator<ISpecifiedItemProps, {}> {
  const getCurrentItemSet = (itemCache: IItemCache, props: {}): ISpecifiedItemProps => {
    return {
      itemKey: getCurrentItemSetKey(itemCache)
    };
  };

  return decorate(getCurrentItemSet);
}

/**
 * @public
 */
export type IDecorateWithCurrentItemSetProps<TProps extends ISpecifiedItemProps> = IDecoratedProps<TProps, ISpecifiedItemProps, {}>;

/**
 * @public
 */
export function withCurrentItemSet(): IConnectToItemCacheDecorator<ISpecifiedItemProps, {}> {
  return <P extends ISpecifiedItemProps>(
    component: React.ComponentType<P>
  ) => {
    return withDisplayName('WithCurrentItemSet')(
      connectToItemCache(
        decorateWithCurrentItemSet()(base)
      )
    )(component);
  };
}

function decorateWithOnlyCurrentItem(): IItemCacheDecorator<ISpecifiedItemProps, {}> {
  const getOnlyCurrentItem = (itemCache: IItemCache, props: ISpecifiedItemProps & ISpecifiedItemsProps): ISpecifiedItemProps => {
    const hasSelection = props.itemKeys.length > 0;

    return {
      itemKey: hasSelection ? undefined : props.itemKey
    };
  };

  return decorate(decorateWithCurrentItem()(decorateWithSelectedItems()(getOnlyCurrentItem)));
}

export type IDecorateWithOnlyCurrentItemProps<TProps extends ISpecifiedItemProps> = IDecoratedProps<TProps, ISpecifiedItemProps, {}>;

/**
 * Binds a component to the current item as a fallback for no input items.
 * @public
 */
export function withOnlyCurrentItem(): IConnectToItemCacheDecorator<ISpecifiedItemProps, {}> {
  return <P extends ISpecifiedItemProps>(
    component: React.ComponentType<P>
  ) => {
    return withDisplayName('WithOnlyCurrentItem')(connectToItemCache(
      decorateWithOnlyCurrentItem()(base)
    ))(component);
  };
}

function decorateWithExactlyOneItem(): IItemCacheDecorator<ISpecifiedItemProps, ISpecifiedItemsProps> {
  const getExactlyOneItem = (itemCache: IItemCache, props: ISpecifiedItemsProps): ISpecifiedItemProps => {
    const {
      itemKeys
    } = props;

    const itemKey = itemKeys.length === 1 ? itemKeys[0] : undefined;

    return {
      itemKey: itemKey
    };
  };

  return decorate(getExactlyOneItem);
}

/**
 * @public
 */
export type IDecorateWithExactlyOneItemProps<TProps extends ISpecifiedItemProps> =
  IDecoratedProps<TProps, ISpecifiedItemProps, ISpecifiedItemsProps>;

/**
 * Binds a component to exactly one of a given input keys.
 * @public
 */
export function withExactlyOneItem(): IConnectToItemCacheDecorator<ISpecifiedItemProps, ISpecifiedItemsProps> {
  return <P extends ISpecifiedItemProps>(
    component: React.ComponentType<P>
  ) => {
    return withDisplayName('WithExactlyOneItem')(
      connectToItemCache(
        decorateWithExactlyOneItem()(base)
      )
    )(component);
  };
}

function decorateWithCurrentItemAsFallback(): IItemCacheDecorator<ISpecifiedItemsProps, ISpecifiedItemsProps> {
  const getCurrentItemAsFallback = (itemCache: IItemCache, props: ISpecifiedItemsProps & ISpecifiedItemProps): ISpecifiedItemsProps => {
    const {
      itemKeys,
      itemKey
    } = props;

    return {
      itemKeys: itemKeys.length > 0 ?
        itemKeys || NO_ITEMS :
        itemKey && (
          [itemKey] // TODO The mapping from item key to this array needs to be memoized. This will require a computed facet.
        ) || NO_ITEMS
    };
  };

  return decorate(decorateWithCurrentItem()(getCurrentItemAsFallback));
}

/**
 * @public
 */
export type IDecorateWithCurrentItemAsFallbackProps<TProps extends ISpecifiedItemsProps> =
  IDecoratedProps<TProps, ISpecifiedItemsProps, ISpecifiedItemsProps>;

/**
 * @public
 */
export function withCurrentItemAsFallback(): IConnectToItemCacheDecorator<ISpecifiedItemsProps, ISpecifiedItemsProps> {
  return <P extends ISpecifiedItemsProps>(
    component: React.ComponentType<P>
  ) => {
    return withDisplayName('WithCurrentItemAsFallback')(
      connectToItemCache(
        decorateWithCurrentItemAsFallback()((itemCache: IItemCache, props: ISpecifiedItemsProps): ISpecifiedItemsProps => {
          return props;
        })
      )
    )(component);
  };
}
